/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 파일업로더 클래스를 정의한다.
 *
 * @file /src/utils/Uploader.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 10. 3.
 */
import { UUID } from '@utils/UUID';
import { Format } from '@utils/Format';
import { Exceptions } from './Exceptions';
import { Ajax } from './Ajax';
import { Language } from './Language';
import { Utils } from './Utils';

/**
 * 업로드상태구조체를 정의한다.
 */
export interface Status {
    /**
     * 상태
     */
    status: 'WAITING' | 'READING' | 'READED' | 'START' | 'UPLOADING' | 'FAILED' | 'CANCELED' | 'COMPLETED';

    /**
     * 해시를 생성중일 경우, 해시생성이 완료된 바이트
     */
    readed: number;

    /**
     * 업로드중이라면, 업로드가 완료된 바이트
     */
    uploaded: number;
}

/**
 * 첨부파일구조체를 정의한다.
 */
export interface Attachment {
    /**
     * 첨부파일고유값 (Draft 상태일 때는 임시파일고유값)
     */
    attachment_id: string;

    /**
     * 파일명
     */
    name: string;

    /**
     * 파일일종류
     */
    type: string;

    /**
     * 파일 MIME 값
     */
    mime: string;

    /**
     * 파일 확장자
     */
    extension: string;

    /**
     * 파일크기
     */
    size: number;

    /**
     * 다운로드 URL
     */
    download: string;

    /**
     * 브라우저에서 볼 수 있는 파일인 경우 보기 URL
     */
    view: string | null;

    /**
     * 이미지 파일인 경우 썸네일 URL
     */
    thumbnail: string | null;

    /**
     * 상태
     */
    status: 'LOCAL' | 'DRAFT' | 'PUBLISHED';
}

/**
 * 파일구조체를 정의한다.
 */
export interface Draft {
    /**
     * 임시파일고유값
     */
    attachment_id: string;

    /**
     * 업로드 URL
     */
    url: string;

    /**
     * 파일해시
     */
    hash: string;

    /**
     * 파일객체
     */
    file: File;

    /**
     * 첨부파일구조체
     */
    attachment: Attachment;

    /**
     * 업로드상태
     */
    status: Status;

    /**
     * 해시생성 중단 컨트롤러
     */
    controller: AbortController;
}

export interface Properties {
    /**
     * 업로드 URL
     */
    url: string;

    /**
     * 청크사이즈
     */
    chunkSize?: number;

    /**
     * 파일의 상태가 변경되었을 때 수신할 이벤트리스너
     */
    update?: (draft_id: string, attachment: Attachment, status: Status) => Promise<void>;
}

export class Uploader {
    /**
     * 업로드 url
     */
    url: string;

    /**
     * 청크사이즈
     */
    chunkSize: number = 5;

    /**
     * 업로드가 완료된 첨부파일객체
     */
    attachments: Map<string, Attachment> = new Map();

    /**
     * 업로드가 실패한 첨부파일객체 (get() 호출시 조회 후 비워진다.)
     */
    failed: Map<string, Attachment> = new Map();

    /**
     * get() 호출로 업로드완료를 기다리고 있는 수.
     * 0보다 크면 현재 누군가 get() 을 통해 결과를 기다리고 있다는 뜻이며,
     * 이 상태에서 업로드가 실패하면 failed 에 쌓아두었다가 그 get() 호출이 예외로 알린다.
     * 0이면 대기중인 get() 호출이 없다는 뜻이며(자동업로드가 폼 전송 이전에 끝난 경우),
     * 그 즉시 실패메시지를 출력한다.
     */
    waiting: number = 0;

    /**
     * 업로드대기열
     */
    drafts: Map<string, Draft> = new Map();

    /**
     * 해시생성객체
     */
    hashes: Map<string, Promise<string>> = new Map();

    /**
     * 해시생성체인
     */
    hasher: () => Promise<void> = null;

    /**
     * 업로드객체
     */
    uploader: () => Promise<void> = null;

    /**
     * 업로드요청
     */
    request: XMLHttpRequest = null;

    /**
     * 현재 업로드중인 고유값
     */
    current_id: string = null;

    /**
     * 파일의 상태가 변경되었을 때 수신할 이벤트리스너
     */
    update: (draft_id: string, attachment: Attachment, status: Status) => Promise<void> = null;

    /**
     * 첨부파일 클래스를 정의한다.
     *
     * @param url - 파일을 처리하기 위한 URL
     */
    constructor(public properties: Properties) {
        this.url = properties.url;
        this.chunkSize = properties.chunkSize ?? 5;
        this.update = properties.update ?? null;
    }

    /**
     * 파일을 대기열에 추가한다.
     *
     * @param files - 추가할 파일객체
     * @return 추가된 파일객체
     */
    add(files: FileList | File[]): Attachment[] {
        const added = [];

        for (const file of files) {
            if (file.size == 0) continue;

            /**
             * 클립보드 데이터인 경우 파일명이 존재하지 않으므로, 가상의 파일명을 생성한다.
             */
            const name = file.name ?? 'clipboard.' + (file.type.split('/')[1] ?? 'png');

            const draft: Draft = {
                attachment_id: UUID.v4(),
                url: null,
                hash: null,
                file: file,
                attachment: null,
                controller: new AbortController(),
                status: {
                    status: 'WAITING',
                    readed: 0,
                    uploaded: 0,
                },
            };

            const attachment: Attachment = {
                attachment_id: draft.attachment_id,
                name: Format.normalizer(name),
                type: this.getType(file.type),
                mime: file.type,
                extension: this.getExtension(name),
                size: file.size,
                download: URL.createObjectURL(draft.file),
                view: null,
                thumbnail: null,
                status: 'LOCAL',
            };

            draft.attachment = attachment;

            if (['image', 'svg', 'icon'].includes(attachment.type) == true) {
                attachment.thumbnail = attachment.view = attachment.download;
            }

            this.drafts.set(draft.attachment_id, draft);
            added.push(attachment);
        }

        this.#start();

        return added;
    }

    /**
     * 대기열에서 파일을 삭제한다.
     *
     * @param draft_id - 취소할 파일고유값
     */
    async delete(draft_id: string): Promise<void> {
        const isStarted = await this.pause();
        const draft = this.drafts.get(draft_id) ?? null;
        if (draft !== null) {
            draft.controller.abort();
            URL.revokeObjectURL(draft.attachment.download);
            this.drafts.delete(draft_id);
        }
        if (isStarted == true) {
            this.start();
        }
    }

    /**
     * 업로드를 시작한다.
     */
    async start(): Promise<void> {
        if (this.uploader !== null) {
            return this.uploader();
        }

        const sharedPromise = this.#upload().finally(() => {
            this.uploader = null;
        });
        this.uploader = () => sharedPromise;

        return sharedPromise;
    }

    /**
     * 진행중인 업로드가 있다면 일시중단한다.
     *
     * @returns 업로드가 진행중이었는지 여부
     */
    async pause(): Promise<boolean> {
        const isStarted = this.uploader !== null;
        this.request?.abort();
        return isStarted;
    }

    /**
     * 아직 업로드가 되지 않은 파일이 있다면 업로드를 계속수행하고, 모든 업로드가 완료되면 첨부파일 객체를 반환한다.
     * 그 사이 업로드가 실패한 파일이 있다면 예외를 발생시킨다.
     *
     * @returns 첨부파일객체
     */
    async get(): Promise<Attachment[]> {
        this.waiting++;
        try {
            await this.start();

            if (this.failed.size > 0) {
                const names = this.failed
                    .values()
                    .toArray()
                    .map((attachment) => attachment.name)
                    .join(', ');
                this.failed.clear();
                throw Exceptions.code('FAILED_TO_UPLOAD_FILE', { name: names });
            }

            const attachments = this.attachments.values().toArray();
            this.attachments.clear();
            return attachments;
        } finally {
            this.waiting--;
        }
    }

    /**
     * 업로드큐에서 파일진행상태를 가져온다.
     *
     * @param draft_id - 임시파일고유값
     * @returns 파일상태
     */
    getStatus(draft_id: string): Status | null {
        return this.drafts.get(draft_id)?.status ?? null;
    }

    /**
     * 파일종류를 가져온다.
     *
     * @param mime - 파일 MIME
     * @return 파일종류
     */
    getType(mime: string): string {
        const types = mime.match(/^(.*?)\/(.*?)$/);
        if (types === null) {
            return 'file';
        }

        const type = types[1];
        const detail = types[2];

        switch (type) {
            case 'image':
                if (detail.search(/svg/) > -1) {
                    return 'svg';
                } else if (detail == 'x-icon') {
                    return 'icon';
                }

                return 'image';

            case 'application':
                if (detail.search(/(pdf|officedocument|opendocument|word|powerpoint|excel|xml|rtf)/) > -1) {
                    return 'document';
                } else if (detail.search(/(zip|rar|tar|compressed)/) > -1) {
                    return 'archive';
                } else if (detail.search(/(json)/) > -1) {
                    return 'text';
                }

                return 'file';

            case 'video':
            case 'audio':
            case 'text':
                return type;
        }

        return 'file';
    }

    /**
     * 파일 확장자를 가져온다.
     *
     * @param name - 파일명
     * @return 확장자
     */
    getExtension(name: string): string {
        const extension = name.split('.').pop().toLowerCase();
        const replacement = {
            'jpeg': 'jpg',
            'htm': 'html',
        };
        return replacement[extension] ?? extension;
    }

    /**
     * 파일해시를 가져온다.
     *
     * @param draft - 파일해시를 생성할 임시파일
     */
    async #hash(draft: Draft): Promise<string> {
        if (draft.hash === null) {
            if (this.hashes.has(draft.attachment_id) == true) {
                return this.hashes.get(draft.attachment_id);
            }

            const hashPromise = (async () => {
                draft.status.status = 'READING';
                await this.#update(draft, { ...draft.status });

                return Format.fileHash(
                    draft.file,
                    (progress) => {
                        draft.status.readed = progress;
                        this.#update(draft, { ...draft.status });
                    },
                    draft.controller.signal
                )
                    .then((hash) => {
                        draft.hash = hash;
                        draft.status.status = 'READED';
                        this.hashes.delete(draft.attachment_id);
                        this.#update(draft, { ...draft.status });
                        return hash;
                    })
                    .catch((e) => {
                        this.hashes.delete(draft.attachment_id);
                        throw e;
                    });
            })();
            this.hashes.set(draft.attachment_id, hashPromise);

            return hashPromise;
        }

        return draft.hash;
    }

    /**
     * 대기열의 파일 해시를 순차적으로 생성한다.
     */
    async #start(): Promise<void> {
        if (this.hasher !== null) {
            return this.hasher();
        }

        const run = async (): Promise<void> => {
            const draft = [...this.drafts.values()].find((d) => d.hash === null) ?? null;
            if (draft === null) {
                this.hasher = null;
                return;
            }
            try {
                await this.#hash(draft);
            } catch (e) {
                if (!(e instanceof DOMException && e.name === 'AbortError')) {
                    throw e;
                }
            }
            return run();
        };

        const sharedPromise = run();
        this.hasher = () => sharedPromise;
        return sharedPromise;
    }

    /**
     * 대기열에서 파일을 업로드한다.
     */
    async #upload(): Promise<void> {
        /**
         * 업로드를 진행할 임시파일 객체를 가져온다.
         */
        const draft: Draft = this.drafts.values().next().value ?? null;
        if (draft === null) {
            this.current_id = null;
            this.request = null;
            return;
        }

        this.current_id = draft.attachment_id;

        /**
         * 파일을 업로드할 URL 이 없다면, 서버에 메타데이터를 주고 업로드할 URL 를 받는다.
         */
        if (draft.url === null) {
            /**
             * 업로드할 파일의 해시가 없다면 해시생성을 대기한다.
             */
            let hash: string;
            try {
                hash = draft.hash ?? (await this.#hash(draft));
            } catch (e) {
                if (e instanceof DOMException && e.name === 'AbortError') {
                    return this.#upload();
                }
                throw e;
            }

            draft.status.status = 'START';
            await this.#update(draft, { ...draft.status });
            await Utils.sleep(1000);

            if (this.drafts.has(draft.attachment_id) == false) {
                return this.#upload();
            }

            try {
                const results = await Ajax.post(this.url, { ...draft.attachment, hash: hash });
                if (this.drafts.has(draft.attachment_id) == false) {
                    return this.#upload();
                }
                if (results.success == true) {
                    /**
                     * 서버에 동일해시파일이 존재한다면, URL 에 첨부파일객체가 정의되고,
                     * 업로드를 진행하지 않고 바로 완료처리한다.
                     */
                    if (typeof results.url == 'object') {
                        URL.revokeObjectURL(draft.attachment.download);
                        draft.attachment = results.url as Attachment;
                        draft.status.status = 'COMPLETED';
                        await this.#complete(draft);
                        return this.#upload();
                    } else {
                        draft.url = results.url;
                    }
                }
            } catch (e) {
                URL.revokeObjectURL(draft.attachment.download);
                draft.status.status = 'FAILED';
                await this.#complete(draft);
                return this.#upload();
            }
        }

        const chunkSize = this.chunkSize * 1000 * 1000;
        const chunkEnd =
            draft.attachment.size > draft.status.uploaded + chunkSize
                ? draft.status.uploaded + chunkSize
                : draft.attachment.size;

        this.request = new XMLHttpRequest();
        this.request.responseType = 'json';
        this.request.open('POST', draft.url, true);
        this.request.setRequestHeader('X-Method', 'PUT');
        this.request.setRequestHeader('Content-Type', 'application/octet-stream');
        this.request.setRequestHeader('Accept-Language', Language.getDefaultLangauge());
        this.request.setRequestHeader(
            'Content-Range',
            'bytes ' + draft.status.uploaded + '-' + (chunkEnd - 1) + '/' + draft.attachment.size
        );
        this.request.upload.addEventListener('progress', (e: ProgressEvent) => {
            this.#update(draft, { ...draft.status, uploaded: draft.status.uploaded + e.loaded });
        });

        return new Promise((resolve) => {
            this.request.addEventListener('error', async (e) => {
                Exceptions.error('Upload error:', e);

                draft.status.status = 'FAILED';
                URL.revokeObjectURL(draft.attachment.download);
                await this.#complete(draft);

                resolve(this.#upload());
            });
            this.request.addEventListener('abort', async () => {
                Exceptions.log('Upload aborted');

                this.uploader = null;
                this.current_id = null;
                this.request = null;

                resolve();
            });

            this.request.addEventListener('load', async () => {
                const results = this.request.response;

                if (results.success == true) {
                    draft.status.status = results.status;
                    draft.status.uploaded = results.uploaded;

                    if (results.status == 'COMPLETED') {
                        URL.revokeObjectURL(draft.attachment.download);
                        draft.attachment = results.attachment;
                        await this.#complete(draft);
                    }
                } else {
                    draft.status.status = 'FAILED';
                    URL.revokeObjectURL(draft.attachment.download);
                    await this.#complete(draft);
                }

                resolve(this.#upload());
            });

            draft.status.status = 'UPLOADING';
            this.#update(draft, { ...draft.status });
            this.request.send(draft.file.slice(draft.status.uploaded, chunkEnd));
        });
    }

    /**
     * 임시파일상태가 변경되었을 때 이벤트를 전파한다.
     *
     * @param draft - 상태가 변경된 임시파일객체
     * @param status - 업로드상태
     */
    async #update(draft: Draft, status: Status): Promise<void> {
        await this.update?.(draft.attachment_id, draft.attachment, status);
    }

    /**
     * 업로드완료처리를 한다.
     *
     * @param draft - 업로드가 완료된 임시파일객체
     */
    async #complete(draft: Draft): Promise<void> {
        this.current_id = null;
        this.request = null;
        if (draft.status.status == 'COMPLETED') {
            this.attachments.set(draft.attachment.attachment_id, draft.attachment);
        } else if (draft.status.status == 'FAILED') {
            /**
             * get() 이 호출되어 결과를 기다리고 있는 상태라면 그 호출이 예외로 알리도록 위임하고,
             * 그렇지 않다면(자동업로드가 폼 전송 전에 실패한 경우) 지금 바로 실패메시지를 출력한다.
             */
            if (this.waiting > 0) {
                this.failed.set(draft.attachment_id, draft.attachment);
            } else {
                Exceptions.print(Exceptions.code('FAILED_TO_UPLOAD_FILE', { name: draft.attachment.name }));
            }
        }
        await this.#update(draft, { ...draft.status });

        await Utils.sleep(500);
        this.drafts.delete(draft.attachment_id);
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.request?.abort();

        for (const draft of this.drafts.values()) {
            draft.controller.abort();
            URL.revokeObjectURL(draft.attachment.download);
        }

        this.drafts.clear();
        this.hashes.clear();
        this.attachments.clear();
        this.failed.clear();

        this.hasher = null;
        this.uploader = null;
        this.request = null;
        this.current_id = null;
        this.update = null;
    }
}
